import { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import socketIOClient from "socket.io-client";
import { usePermissions } from "../components/PermissionGate";
import { SCOPES, SOCKETURL } from "../contants";
import { getOfflineActiveOrders } from "../redux/action/offlineDataActions";
import {
  activateOrder,
  activateOrderSocket,
  changeItemQuantity,
  changeItemQuantitySocket,
  deleteLocalOrderSocket,
  getKotNumber,
  getSocketOrders,
  pushItemToActiveOrderSocket,
  removeItemSocket,
  setItemAsPreparedSocket,
  setKOTitemsDataRedux,
  setTableOrderInfo,
} from "../redux/action/orderActions";
import { orderTypes } from "../redux/types";
import {
  setKOTPrintData,
  setPrintData,
  toggleSocketConnected,
  toggleSocketDisconnected,
} from "../redux/action/utilActions";
import useIsTabActive from "./useTabActive";

const NEW_CHAT_MESSAGE_EVENT = "NEW_CHAT_MESSAGE_EVENT";

const SEND_TO_CUSTOMER_VIEW = "SEND_TO_CUSTOMER_VIEW";

const SEND_TO_ADMIN_VIEW = "SEND_TO_ADMIN_VIEW";

const SOCKET_SERVER_URL = SOCKETURL;

const useKitchenDisplay = (roomId) => {
  const [messages, setMessages] = useState();
  const isElectron = window?.api?.isElectron;
  const isTabVisible = useIsTabActive();
  const { isOnline, isAppOffline } = useSelector(
    (state) => state.networkStatus,
  );
  const [customerMessages, setCustomerMessages] = useState([]);
  const hasPermission = usePermissions({
    scopes: [SCOPES.KITCHEN_DISPLAY_SYSTEM],
  });

  const dispatch = useDispatch();
  const { branchId, restaurantId, id, role } = useSelector(
    (state) => state.user,
  );
  const socketRef = useRef();
  const joinedRoomsRef = useRef(new Set());
  const isKitchenUser = role === "kitchenuser";

  const joinRoomsAndFetchOrders = useCallback(() => {
    if (!socketRef.current || !socketRef.current.connected) return;

    const channels = [id, branchId];
    for (const ch of channels) {
      if (!joinedRoomsRef.current.has(ch)) {
        socketRef.current.emit("JOIN_ROOM", ch);
        joinedRoomsRef.current.add(ch);
      }
    }

    socketRef.current.emit("GET_ORDERS", {
      branchId,
      restaurantId,
      userId: id,
    });
  }, [id, branchId, restaurantId]);

  const setupEventListeners = useCallback(() => {
    if (!socketRef.current) {
      // alert("Socket is not available");
      return;
    }

    if (!isKitchenUser) {
      socketRef.current.on(orderTypes.ACTIVATE_ORDER, (message) => {
        console.log("message", message);
        dispatch(activateOrderSocket(message));
      });

      socketRef.current.on(orderTypes.PUSH_ITEM_TO_ORDER, (message) => {
        console.log("push received", message);
        dispatch(pushItemToActiveOrderSocket(message));
      });

      socketRef.current.on(orderTypes.CHANGE_ITEM_QUANTITY, (message) => {
        dispatch(changeItemQuantitySocket(message));
      });

      socketRef.current.on(orderTypes.REMOVE_ITEM, (message) => {
        dispatch(removeItemSocket(message));
      });

      socketRef.current.on(orderTypes.DELETE_LOCAL_ORDER, (message) => {
        dispatch(deleteLocalOrderSocket(message?.refId));
      });

      socketRef.current.on(orderTypes.SET_KOT_ITEMS, (message) => {
        dispatch(setKOTitemsDataRedux(message));
      });

      socketRef.current.on("SET_KOT_ITEMS_UPDATED", (message) => {
        if (message?.print && role === "branchadmin") {
          dispatch(setKOTPrintData(message?.data));
        }
      });

      socketRef.current.on("SET_PRINT_DATA_FOR_ADMIN", (message) => {
        if (role === "branchadmin") {
          dispatch(setPrintData(message));
        }
      });

      socketRef.current.on(SEND_TO_ADMIN_VIEW, (message) => {
        setMessages(message);
      });

      socketRef.current.on(SEND_TO_CUSTOMER_VIEW, (message) => {
        setCustomerMessages(message);
      });
    }
    if (hasPermission) {
      socketRef.current.on(orderTypes.SET_ITEM_AS_PREPARED, (message) => {
        dispatch(setItemAsPreparedSocket(message));
      });
    }
    socketRef.current.on("GET_ORDERS", (message) => {
      // console.log("getsocket", message);
      setCustomerMessages(message);
      dispatch(getSocketOrders(message));
    });

    socketRef.current.on("GET_TABLE_ORDER_INFO", (message) => {
      console.log("GET_TABLE_ORDER_INFO", message);
      // setCustomerMessages(message);
      if (message.status === 200) {
        dispatch(setTableOrderInfo(message));
      }
    });

    socketRef.current.on("GET_KOT_NUMBER", (message) => {
      // console.log("kot number", message);
      dispatch(getKotNumber(message));
    });

    socketRef.current.on("SOCKET_ERROR", (message) => {
      alert(`Error ${message}`);
    });
  }, [dispatch, isKitchenUser, hasPermission, role]);

  useEffect(() => {
    if (isElectron && (isAppOffline || !isOnline)) {
      dispatch(getOfflineActiveOrders(window.api.readItemData("activeOrders")));
    } else {
      socketRef.current = socketIOClient(SOCKET_SERVER_URL, {
        query: { userId: id },
        reconnection: true,
        reconnectionDelay: 1000,
      });

      setupEventListeners();

      socketRef.current.on("connect", () => {
        console.log("Socket connected");
        dispatch(toggleSocketConnected());
        joinRoomsAndFetchOrders();
      });

      socketRef.current.on("reconnect", (attemptNumber) => {
        console.log(`Socket reconnected after ${attemptNumber} attempts`);
        dispatch(toggleSocketConnected());
        joinRoomsAndFetchOrders();
      });

      socketRef.current.on("disconnect", (reason) => {
        console.log("Socket disconnected:", reason);
        dispatch(toggleSocketDisconnected());
        joinedRoomsRef.current.clear();
      });

      return () => {
        if (socketRef.current) {
          if (socketRef.current.connected) {
            for (const ch of joinedRoomsRef.current) {
              socketRef.current.emit("LEAVE_ROOM", ch);
            }
          }
          socketRef.current.disconnect();
        }
      };
    }
  }, [
    roomId,
    isElectron,
    isAppOffline,
    isOnline,
    dispatch,
    setupEventListeners,
    joinRoomsAndFetchOrders,
  ]);

  useEffect(() => {
    if (isTabVisible && socketRef.current && socketRef.current.disconnected) {
      // alert("PWA became visible, forcing socket reconnect...");
      // For PWAs, explicit disconnect ensures we clear zombie state before reconnecting
      if (!socketRef.current.connected) {
        socketRef.current.disconnect();
        socketRef.current.connect();
      }
    }
  }, [isTabVisible]);

  const sendToAdmin = (messageBody) => {
    console.log("messageBody", messageBody);
    if (!socketRef.current) return;
    socketRef.current.emit(SEND_TO_ADMIN_VIEW, messageBody);
  };

  const sendToCustomer = (messageBody) => {
    if (!socketRef.current) return;
    socketRef.current.emit(SEND_TO_CUSTOMER_VIEW, messageBody);
  };

  const clearMessages = (messageBody) => {
    setMessages();
    setCustomerMessages();
  };

  const getAllOrders = () => {
    socketRef.current.emit("GET_ORDERS", {
      branchId,
      restaurantId,
      userId: id,
    });
  };

  return { getAllOrders, customerMessages };
};

export default useKitchenDisplay;
